import React, { useState } from 'react';
import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import Img from 'gatsby-image';
import { ReferralPageQuery } from '../../graphql-types';

import Button from '../ui/BaseButton';
import Layout from '../components/layout';
import SEO from '../components/seo';
import Heading2 from '../ui/Heading2';
import { BREAKPOINTS, COLORS } from '../ui/variables';
import ParagraphExLarge from '../ui/ParagraphExLarge';
import Blockquote from '../ui/Blockquote';
import OrangeCheckmarkIco from '../images/icons/orange-checkmark-ico.svg';
import Heading3 from '../ui/Heading3';

const Container = styled.section`
  background: ${COLORS.LIGHT_BEIGE};
  margin-bottom: 60px;
  padding: 100px 0 120px 0;

  @media ${BREAKPOINTS.MOBILE} {
    padding-left: 20px;
    padding-right: 20px;
    padding-top: 50px;
    padding-bottom: 82%;
  }
`;

const Wrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin: auto;
  max-width: 1240px;
`;

const Content = styled.div`
  width: 60%;
  padding-right: 40px;

  @media ${BREAKPOINTS.MOBILE} {
    width: 100%;
    padding: 0;
    flex-direction: column;
    display: flex;
  }
`;

const Form = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 60px;
`;

const Input = styled.input`
  height: 56px;
  color: ${COLORS.BLACK};
  background: ${COLORS.WHITE};
  border: 1px solid ${COLORS.ORANGE};
  padding: 0 16px;
  line-height: 56px;
  outline: none;
  border-radius: 3px;
  font-size: 16px;
  font-weight: 400;
  width: 350px;
  margin-bottom: 16px;

  &::placeholder {
    color: ${COLORS.DARK_GRAY};
  }
`;

const Submit = styled(Button)`
  width: 350px;
  font-size: 18px;
  border: none;
  padding: 18px 0;
  margin-top: 16px;
  cursor: pointer;
`;

const Additional = styled.section`
  padding: 60px 20px 30px 20px;
  max-width: 1240px;
  display: flex;
  align-items: center;
  flex-direction: column;
  margin: auto;
`;

const Success = styled.div`
  border-radius: 4px;
  background-color: #c7e6c7;
  color: #5cb85c;
  padding: 6px 12px;
`;

const Services = styled.ul`
  display: flex;
  flex-wrap: wrap;
  margin: auto;
  list-style: none;
  justify-content: space-between;

  @media ${BREAKPOINTS.TABLET} {
    max-width: 700px;
  }

  @media screen and (max-width: 660px) {
    flex-direction: column;
    padding-left: 0;
  }

  li {
    width: 45%;
    margin-bottom: 30px;
    padding-left: 44px;
    background-repeat: no-repeat;
    background-position: 0 10%;
    background-image: url(${OrangeCheckmarkIco});
    background-size: 21px;

    @media ${BREAKPOINTS.TABLET} {
      width: 50%;
    }

    @media screen and (max-width: 660px) {
      width: 100%;
    }
  }
`;

export const pageQuery = graphql`
  query ReferralPage {
    dogSniffingKitten: file(
      relativePath: { eq: "photos/dog-sniffing-kitten.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 600) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
    ladyLayingWithDog: file(
      relativePath: { eq: "pages/about-us/lady-dog-laying.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1800) {
          ...GatsbyImageSharpFluid_withWebp
        }
      }
    }
  }
`;

interface Props {
  data: ReferralPageQuery;
}

const copy = (
  <React.Fragment>
    <ParagraphExLarge>
      Congrats! Get your <b>first exam free</b>, plus a{' '}
      <b>free one-year Access membership</b>!
    </ParagraphExLarge>
    <ParagraphExLarge>Use coupon code ADOPTED at checkout.</ParagraphExLarge>
    <ParagraphExLarge>
      Proof of adoption required. Pets eligible within one month of adoption
      from registered rescues/shelters.
    </ParagraphExLarge>
  </React.Fragment>
);

const IndexPage = ({ data }: Props) => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [fullNameError, setFullNameError] = useState(false);
  const [refereeEmailError, setRefereeEmailError] = useState(false);
  const [payload, setPayload] = useState({
    member_email: null,
    member_name: null,
    referee_email: null,
  });

  const onChange = (name: string, value: string) => {
    setPayload(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const onSubmit = async () => {
    if (loading) {
      return;
    }

    let error = false;
    if (!payload.member_email?.match(/^\S+@\S+\.\S+$/)) {
      setEmailError(true);
      error = true;
    } else {
      setEmailError(false);
    }

    if (!payload.member_name?.match(/^[a-zA-Z\s]+$/)) {
      setFullNameError(true);
      error = true;
    } else {
      setFullNameError(false);
    }

    if (!payload.referee_email?.match(/^\S+@\S+\.\S+$/)) {
      setRefereeEmailError(true);
      error = true;
    } else {
      setRefereeEmailError(false);
    }

    if (error) {
      return;
    }

    setLoading(true);
    setEmailError(false);
    setFullNameError(false);
    setRefereeEmailError(false);
    const resp = await fetch(`${process.env.GATSBY_SD_ROOT}/api/v1/nl/refer`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
    setLoading(false);

    if (!resp.ok) {
      window.alert('There was an error, please try again.');
      return;
    }

    setSuccess(true);
    setPayload({
      member_email: null,
      member_name: null,
      referee_email: null,
    });
  };

  return (
    <Layout>
      <SEO
        title='Referrals'
        description="Love Small Door? Refer us to a friend and we'll give you $100 each to put towards your next visit."
      />
      <Container>
        <Wrap>
          <Content>
            <Heading2
              css={css`
                @media ${BREAKPOINTS.MOBILE} {
                  text-align: center;
                }
              `}
            >
              Share the Small Door love
            </Heading2>
            <ParagraphExLarge>
              Refer a friend and if they join, we'll give you $100 each!{' '}
            </ParagraphExLarge>

            {success && (
              <Success>Thanks! We'll email your friend soon.</Success>
            )}

            {!success && (
              <Form>
                <Input
                  type='text'
                  value={payload.member_email}
                  onChange={e => onChange('member_email', e.target.value)}
                  placeholder='Your email'
                  css={css`
                    border-color: ${emailError ? '#d9534f' : COLORS.BLACK};
                  `}
                />
                <Input
                  type='text'
                  value={payload.member_name}
                  onChange={e => onChange('member_name', e.target.value)}
                  placeholder='Your full name'
                  css={css`
                    border-color: ${fullNameError ? '#d9534f' : COLORS.BLACK};
                  `}
                />
                <Input
                  type='text'
                  value={payload.referee_email}
                  onChange={e => onChange('referee_email', e.target.value)}
                  placeholder="Your friend's email"
                  css={css`
                    border-color: ${refereeEmailError
                      ? '#d9534f'
                      : COLORS.BLACK};
                  `}
                />
                <Submit type='action' color={COLORS.ORANGE} onClick={onSubmit}>
                  Refer your friend
                </Submit>
              </Form>
            )}
          </Content>
          <Img
            css={css`
              width: 45%;
            `}
            fluid={data.dogSniffingKitten.childImageSharp.fluid}
          />
        </Wrap>
      </Container>

      <Additional>
        <Heading3
          css={css`
            text-align: center;
            margin-bottom: 40px;
          `}
        >
          How does it work?
        </Heading3>

        <Services>
          <li>
            <ParagraphExLarge>
              Provide your friend's email, and we'll send them details about the
              program.
            </ParagraphExLarge>
          </li>
          <li>
            <ParagraphExLarge>
              When they join, they'll just need to give your name on their new
              member form.
            </ParagraphExLarge>
          </li>
          <li>
            <ParagraphExLarge>
              You'll each get $100 credit to put towards your next visit.
            </ParagraphExLarge>
          </li>
          <li>
            <ParagraphExLarge>
              Refer as many friends as you want, and get $100 per new member!
            </ParagraphExLarge>
          </li>
        </Services>

        <Blockquote
          css={css`
            margin: 120px 0;
          `}
        >
          Thanks for sharing the Small Door love!
        </Blockquote>
      </Additional>

      <Img fluid={data.ladyLayingWithDog.childImageSharp.fluid} />
    </Layout>
  );
};

export default IndexPage;
